import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageLoader } from "../components/PageLoader";
import { SearchSelect } from "../components/SearchSelect";
import { AccessNotice, fetchApi, reasonOf } from "../components/marketing/access";
import { money4 } from "../components/wallet/api";
import { NO_DATA } from "../lib/format";

/**
 * Wallet → Rates by country (/wallet/pricing), the old pages/waba/Wallet/PricingView.js in the Olli design.
 *
 *   rates per country and category, the clinic's discount ... GET /api/waba/pricing → pricing:
 *     [{ country_code, country_name, category, base_price, final_price, has_discount, discount_info }]
 *   free service conversations ............................. same call, freeTier
 * India is picked first when listed (as the old page), else the first country. Amounts are USD.
 */
const CATEGORIES = [
  { key: "marketing", label: "Marketing", uses: "Offers, campaigns and product updates" },
  { key: "utility", label: "Utility", uses: "Reminders, receipts, follow-ups" },
  { key: "authentication", label: "Authentication", uses: "Login and verification codes" },
  { key: "service", label: "Service", uses: "Replies within 24 hours of the patient's message" },
];

export default function WalletPricing() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [country, setCountry] = useState("");

  const load = useCallback(async () => {
    setError(null); setData(null);
    const r = await fetchApi("/api/waba/pricing");
    if (!r.ok) { setError(reasonOf(r)); setData({ pricing: [] }); return; }
    const pricing = r.data?.pricing || [];
    setData({ pricing, freeTier: r.data?.freeTier || null });
    setCountry((c) => c || (pricing.some((p) => p.country_code === "IN") ? "IN" : pricing[0]?.country_code || ""));
  }, []);
  useEffect(() => { load(); }, [load]);

  const countries = useMemo(() => {
    const seen = new Map();
    for (const p of data?.pricing || []) if (!seen.has(p.country_code)) seen.set(p.country_code, p.country_name || p.country_code);
    return [...seen].map(([value, name]) => ({ value, label: name, hint: value })).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);
  const rows = (data?.pricing || []).filter((p) => p.country_code === country);

  return (
    <div className="mx-auto flex max-w-5xl animate-fade-in flex-col gap-6 px-6 py-9 md:px-8">
      <header className="flex flex-col gap-3">
        <Link to="/wallet" className="flex w-fit items-center gap-1.5 text-small font-medium text-muted hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Wallet</Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="text-h1 font-semibold">WhatsApp rates by country</h1><p className="mt-1 text-body text-muted">What your clinic pays per delivered message, by the patient's country</p></div>
          {countries.length > 0 && <div className="w-full sm:w-72"><SearchSelect value={country} options={countries} onChange={setCountry} searchable searchPlaceholder="Search countries" ariaLabel="Country" /></div>}
        </div>
      </header>

      {data === null ? <PageLoader label="Loading rates…" /> : error ? <AccessNotice reason={error} onRetry={load} /> : countries.length === 0 ? <p className="rounded-lg bg-paper px-6 py-12 text-center text-small text-muted shadow-soft">No rates are set up yet.</p> : (
        <>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CATEGORIES.map((c) => {
              const rate = rows.find((p) => p.category === c.key);
              const free = c.key === "service" && (!rate || Number(rate.final_price) === 0);
              return <li key={c.key} className="flex flex-col gap-2 rounded-lg bg-paper p-5 shadow-soft">
                <span className="text-small font-semibold">{c.label}</span>
                <p className="flex items-baseline gap-2"><span className="text-h2 font-semibold tabular-nums">{free ? "Free" : rate ? money4(rate.final_price) : NO_DATA}</span>{!free && rate && <span className="text-caption text-muted">per message</span>}</p>
                {rate?.has_discount && <p className="text-caption"><span className="text-muted line-through">{money4(rate.base_price)}</span> <span className="font-semibold text-success">{rate.discount_info || "Your clinic's rate"}</span></p>}
                <p className="text-caption text-muted">{c.uses}</p>
              </li>;
            })}
          </ul>
          <section className="rounded-lg bg-paper p-6 shadow-soft">
            <h2 className="text-h3 font-semibold">How charges work</h2>
            <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-small text-muted">
              <li>Marketing, utility and authentication messages use approved templates and are charged when delivered.</li>
              <li>Replies inside 24 hours of the patient's last message are service messages: free-text and free.</li>
              <li>Utility templates sent inside that 24-hour window are free.</li>
              <li>Failed messages are refunded to the wallet automatically.</li>
              {data.freeTier?.limit ? <li>Free service conversations this month: {Number(data.freeTier.used || 0).toLocaleString("en-IN")} of {Number(data.freeTier.limit).toLocaleString("en-IN")} used.</li> : null}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
